/**
 * Theme Composable - Vuetify 3.5 Integration
 *
 * @version 2.1.0
 * @description Composable for accessing Vuetify theme colors and managing theme state.
 *              Provides full backward compatibility with legacy `theme` object.
 *
 * ARCHITECTURE:
 * - Source of truth: plugins/vuetify.ts (BRAND_COLORS, theme definitions)
 * - This composable: reactive access to Vuetify theme API
 * - Legacy support: `theme` object computed from current Vuetify theme
 *
 * @see https://vuetifyjs.com/en/features/theme/
 */

import { computed, type ComputedRef } from 'vue'
import { useTheme as useVuetifyTheme } from 'vuetify'
import { BRAND_COLORS, THEME_STORAGE_KEY, type ThemeLayerName, type AllThemeNames } from '@/plugins/vuetify'

// =============================================================================
// Types
// =============================================================================

export interface ThemeColors {
  primary: string
  primaryDarken1: string
  primaryLighten1: string
  secondary: string
  secondaryDarken1: string
  secondaryLighten1: string
  accent: string
  error: string
  info: string
  success: string
  warning: string
  background: string
  surface: string
  onBackground: string
  onSurface: string
  onPrimary: string
  onSecondary: string
  // Brand colors
  ctfBrand: string
  mktBrand: string
  srvBrand: string
  sysBrand: string
  // UI colors
  toolbarBg: string
  drawerBg: string
  tableHeader: string
  tableBorder: string
  tableHover: string
  inputBg: string
}

/**
 * Legacy theme object interface for backward compatibility
 * Used by Options API components that access `this.theme`
 */
export interface LegacyTheme {
  // Brand colors
  primaryColor: string
  primaryDarkColor: string
  secondaryColor: string
  accentColor: string

  // Sub-brand colors
  ctfColor: string
  mktColor: string
  srvColor: string
  sysColor: string

  // Semantic colors
  successColor: string
  infoColor: string
  warningColor: string
  errorColor: string
  dangerColor: string

  // Text colors
  textColor: string
  textSecondaryColor: string

  // Background colors
  headerContentColor: string
  pageContentColor: string
  barColor: string

  // Footer
  footerHeight: number
  footerColor: string
  footerTextColor: string

  // Menu
  menuColor: string
  userMenuBG: string
  userMenuTextColor: string

  // Layout
  boxedMaxWidth: number
  fullMaxWidth: number

  // Snackbar
  snackBarColorSuccess: string
  snackBarColorError: string
  snackBarColorInfo: string
  snackBarColorWarning: string

  // Data tables
  tableBordersColor: string
  tableHeaderBg: string
  tableHoverBg: string

  // Row status colors
  enabledRowColor: string
  disabledRowColor: string
  expiryOKColor: string
  expiryKOColor: string

  // Pagination
  maximumPaginationPages: number

  // Default DataTable options
  defaultDatatableOptions: {
    page: number
    itemsPerPage: number
    sortBy: readonly any[]
    groupBy: readonly any[]
    search: string
  }

  // Left expansion panels
  leftExpansionPanels: {
    textFieldBackgroundColor: string
  }
}

export interface UseThemeReturn {
  /** Current theme name */
  currentTheme: ComputedRef<string>
  /** Whether dark mode is active */
  isDark: ComputedRef<boolean>
  /** Current theme colors (reactive) */
  colors: ComputedRef<ThemeColors>
  /** Raw Vuetify theme colors object */
  rawColors: ComputedRef<Record<string, string>>
  /** Legacy theme object for backward compatibility */
  theme: ComputedRef<LegacyTheme>
  /** Toggle between light and dark themes */
  toggleTheme: () => void
  /** Set a specific theme by name */
  setTheme: (name: AllThemeNames) => void
  /** Check if a theme layer is active */
  isLayer: (layer: ThemeLayerName) => boolean
  /** Get CSS variable value for a color */
  getCssVar: (colorName: string) => string
  /** Get RGB values for use in rgba() */
  getRgb: (colorName: string) => string
}

// =============================================================================
// Static Layout Configuration (non-color values)
// =============================================================================

/**
 * Default items per page for all data tables.
 * Single source of truth — imported by useListPage.createDefaultOptions().
 */
export const DEFAULT_ITEMS_PER_PAGE = 15

const LAYOUT_CONFIG = {
  footerHeight: 40,
  boxedMaxWidth: 1600,
  fullMaxWidth: 2560,
  maximumPaginationPages: 8,
  defaultDatatableOptions: {
    page: 1,
    itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
    sortBy: [],
    groupBy: [],
    search: '',
  },
} as const

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get a color from Vuetify theme colors with fallback
 */
function getColor(colors: Record<string, string>, key: string, fallback: string): string {
  return colors[key] ?? fallback
}

// =============================================================================
// Composable
// =============================================================================

/**
 * Theme composable for Vue 3 Composition API
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { useTheme } from '@/themes/theme-mixin'
 *
 * const { theme, isDark, toggleTheme, colors, setTheme } = useTheme()
 *
 * // Access colors reactively
 * console.log(colors.value.primary) // '#344955'
 *
 * // Legacy compatibility (for Options API components)
 * console.log(theme.value.primaryColor) // '#344955'
 *
 * // Toggle dark mode
 * toggleTheme()
 *
 * // Apply theme layer
 * setTheme('marketingLayer')
 *
 * // Use CSS variable in style
 * // style="color: rgb(var(--v-theme-primary))"
 * </script>
 * ```
 */
export function useTheme(): UseThemeReturn {
  // Get Vuetify theme instance
  const vuetifyTheme = useVuetifyTheme()

  // ==========================================================================
  // Computed: Current Theme State
  // ==========================================================================

  const currentTheme = computed(() => vuetifyTheme.global.name.value)

  const isDark = computed(() => vuetifyTheme.global.current.value.dark)

  // ==========================================================================
  // Computed: Raw Colors from Vuetify
  // ==========================================================================

  const rawColors = computed(() => vuetifyTheme.global.current.value.colors)

  // ==========================================================================
  // Computed: Typed Colors Object
  // ==========================================================================

  const colors = computed<ThemeColors>(() => {
    const c = rawColors.value
    return {
      primary: getColor(c, 'primary', BRAND_COLORS.primary),
      primaryDarken1: getColor(c, 'primary-darken-1', BRAND_COLORS.primaryDark),
      primaryLighten1: getColor(c, 'primary-lighten-1', BRAND_COLORS.primaryLight),
      secondary: getColor(c, 'secondary', BRAND_COLORS.secondary),
      secondaryDarken1: getColor(c, 'secondary-darken-1', BRAND_COLORS.secondaryDark),
      secondaryLighten1: getColor(c, 'secondary-lighten-1', BRAND_COLORS.secondaryLight),
      accent: getColor(c, 'accent', BRAND_COLORS.accent),
      error: getColor(c, 'error', BRAND_COLORS.error),
      info: getColor(c, 'info', BRAND_COLORS.info),
      success: getColor(c, 'success', BRAND_COLORS.success),
      warning: getColor(c, 'warning', BRAND_COLORS.warning),
      background: getColor(c, 'background', '#FFFFFF'),
      surface: getColor(c, 'surface', '#FFFFFF'),
      onBackground: getColor(c, 'on-background', BRAND_COLORS.primaryDark),
      onSurface: getColor(c, 'on-surface', BRAND_COLORS.primaryDark),
      onPrimary: getColor(c, 'on-primary', '#FFFFFF'),
      onSecondary: getColor(c, 'on-secondary', BRAND_COLORS.primaryDark),
      ctfBrand: getColor(c, 'ctf-brand', BRAND_COLORS.ctf),
      mktBrand: getColor(c, 'mkt-brand', BRAND_COLORS.mkt),
      srvBrand: getColor(c, 'srv-brand', BRAND_COLORS.srv),
      sysBrand: getColor(c, 'sys-brand', BRAND_COLORS.sys),
      toolbarBg: getColor(c, 'toolbar-bg', '#FFFFFF'),
      drawerBg: getColor(c, 'drawer-bg', '#FFFFFF'),
      tableHeader: getColor(c, 'table-header', BRAND_COLORS.grey[100]),
      tableBorder: getColor(c, 'table-border', BRAND_COLORS.grey[300]),
      tableHover: getColor(c, 'table-hover', BRAND_COLORS.grey[50]),
      inputBg: getColor(c, 'input-bg', BRAND_COLORS.grey[100]),
    }
  })

  // ==========================================================================
  // Computed: Legacy Theme Object (Backward Compatibility)
  // ==========================================================================

  const theme = computed<LegacyTheme>(() => {
    const c = colors.value
    return {
      // Brand colors
      primaryColor: c.primary,
      primaryDarkColor: c.primaryDarken1,
      secondaryColor: c.secondary,
      accentColor: c.accent,

      // Sub-brand colors
      ctfColor: c.ctfBrand,
      mktColor: c.mktBrand,
      srvColor: c.srvBrand,
      sysColor: c.sysBrand,

      // Semantic colors
      successColor: c.success,
      infoColor: c.info,
      warningColor: c.warning,
      errorColor: c.error,
      dangerColor: c.error,

      // Text colors
      textColor: c.onBackground,
      textSecondaryColor: BRAND_COLORS.grey[600],

      // Background colors
      headerContentColor: c.surface,
      pageContentColor: c.surface,
      barColor: c.toolbarBg,

      // Footer
      footerHeight: LAYOUT_CONFIG.footerHeight,
      footerColor: c.primary,
      footerTextColor: c.onPrimary,

      // Menu
      menuColor: c.surface,
      userMenuBG: c.tableHeader,
      userMenuTextColor: c.onSurface,

      // Layout
      boxedMaxWidth: LAYOUT_CONFIG.boxedMaxWidth,
      fullMaxWidth: LAYOUT_CONFIG.fullMaxWidth,

      // Snackbar
      snackBarColorSuccess: 'success',
      snackBarColorError: 'error',
      snackBarColorInfo: 'info',
      snackBarColorWarning: 'warning',

      // Data tables
      tableBordersColor: c.tableBorder,
      tableHeaderBg: c.tableHeader,
      tableHoverBg: c.tableHover,

      // Row status colors
      enabledRowColor: c.success,
      disabledRowColor: c.error,
      expiryOKColor: '#C8E6C9',
      expiryKOColor: '#FFCDD2',

      // Pagination
      maximumPaginationPages: LAYOUT_CONFIG.maximumPaginationPages,

      // Default DataTable options
      defaultDatatableOptions: { ...LAYOUT_CONFIG.defaultDatatableOptions },

      // Left expansion panels
      leftExpansionPanels: {
        textFieldBackgroundColor: c.inputBg,
      },
    }
  })

  // ==========================================================================
  // Helpers — OS preference detection & persistence
  // ==========================================================================

  /**
   * Returns the OS-level preferred theme.
   * Uses `prefers-color-scheme` media query.
   */
  function _getOsTheme(): 'light' | 'dark' {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }

  /**
   * Persist theme choice to localStorage ONLY if it differs from OS preference.
   * If it matches OS preference, remove the stored value so that future
   * sessions follow OS changes automatically.
   */
  function _persistTheme(themeName: string): void {
    try {
      const osTheme = _getOsTheme()
      if (themeName === osTheme) {
        localStorage.removeItem(THEME_STORAGE_KEY)
      } else {
        localStorage.setItem(THEME_STORAGE_KEY, themeName)
      }
    } catch {
      // localStorage not available — ignore silently
    }
  }

  /**
   * Apply a Vuetify theme by name.
   * Uses Vuetify 3.5+ API: theme.change() with fallback.
   */
  function _applyTheme(name: string): void {
    if (typeof vuetifyTheme.change === 'function') {
      vuetifyTheme.change(name)
    } else {
      vuetifyTheme.global.name.value = name
    }
  }

  // ==========================================================================
  // Methods
  // ==========================================================================

  /**
   * Toggle between light and dark themes.
   * Persists to localStorage only if the new theme differs from OS preference.
   */
  function toggleTheme(): void {
    const newTheme = isDark.value ? 'light' : 'dark'
    _applyTheme(newTheme)
    _persistTheme(newTheme)
  }

  /**
   * Set a specific theme by name.
   * Persists base themes (light/dark) to localStorage;
   * theme layers are transient and not persisted.
   */
  function setTheme(name: AllThemeNames): void {
    _applyTheme(name)
    // Only persist base themes, not layers
    if (name === 'light' || name === 'dark') {
      _persistTheme(name)
    }
  }

  /**
   * Check if a theme layer is currently active
   */
  function isLayer(layer: ThemeLayerName): boolean {
    return currentTheme.value === layer
  }

  /**
   * Get CSS variable syntax for a color
   * @example getCssVar('primary') => 'var(--v-theme-primary)'
   */
  function getCssVar(colorName: string): string {
    return `var(--v-theme-${colorName})`
  }

  /**
   * Get RGB values for use in rgba()
   * @example getRgb('primary') => 'rgb(var(--v-theme-primary))'
   */
  function getRgb(colorName: string): string {
    return `rgb(var(--v-theme-${colorName}))`
  }

  // ==========================================================================
  // Return
  // ==========================================================================

  return {
    currentTheme,
    isDark,
    colors,
    rawColors,
    theme,
    toggleTheme,
    setTheme,
    isLayer,
    getCssVar,
    getRgb,
  }
}

// =============================================================================
// Static Export for Non-Composition API Usage
// =============================================================================

/**
 * Static theme object for use outside of Vue components
 * (e.g., in utility functions, store actions, etc.)
 *
 * @deprecated Use useTheme() composable in components
 */
export const theme: LegacyTheme = {
  // Brand colors
  primaryColor: BRAND_COLORS.primary,
  primaryDarkColor: BRAND_COLORS.primaryDark,
  secondaryColor: BRAND_COLORS.secondary,
  accentColor: BRAND_COLORS.accent,

  // Sub-brand colors
  ctfColor: BRAND_COLORS.ctf,
  mktColor: BRAND_COLORS.mkt,
  srvColor: BRAND_COLORS.srv,
  sysColor: BRAND_COLORS.sys,

  // Semantic colors
  successColor: BRAND_COLORS.success,
  infoColor: BRAND_COLORS.info,
  warningColor: BRAND_COLORS.warning,
  errorColor: BRAND_COLORS.error,
  dangerColor: BRAND_COLORS.error,

  // Text colors
  textColor: BRAND_COLORS.primaryDark,
  textSecondaryColor: BRAND_COLORS.grey[600],

  // Background colors
  headerContentColor: BRAND_COLORS.white,
  pageContentColor: BRAND_COLORS.white,
  barColor: BRAND_COLORS.white,

  // Footer
  footerHeight: LAYOUT_CONFIG.footerHeight,
  footerColor: BRAND_COLORS.primary,
  footerTextColor: BRAND_COLORS.white,

  // Menu
  menuColor: BRAND_COLORS.white,
  userMenuBG: BRAND_COLORS.grey[100],
  userMenuTextColor: BRAND_COLORS.primaryDark,

  // Layout
  boxedMaxWidth: LAYOUT_CONFIG.boxedMaxWidth,
  fullMaxWidth: LAYOUT_CONFIG.fullMaxWidth,

  // Snackbar
  snackBarColorSuccess: 'success',
  snackBarColorError: 'error',
  snackBarColorInfo: 'info',
  snackBarColorWarning: 'warning',

  // Data tables
  tableBordersColor: BRAND_COLORS.grey[300],
  tableHeaderBg: BRAND_COLORS.grey[100],
  tableHoverBg: BRAND_COLORS.grey[50],

  // Row status colors
  enabledRowColor: BRAND_COLORS.success,
  disabledRowColor: BRAND_COLORS.error,
  expiryOKColor: '#C8E6C9',
  expiryKOColor: '#FFCDD2',

  // Pagination
  maximumPaginationPages: LAYOUT_CONFIG.maximumPaginationPages,

  // Default DataTable options
  defaultDatatableOptions: { ...LAYOUT_CONFIG.defaultDatatableOptions },

  // Left expansion panels
  leftExpansionPanels: {
    textFieldBackgroundColor: BRAND_COLORS.grey[100],
  },
}

export default theme
